import React, { FC, useMemo, useState } from 'react';

import { useSearchParam } from 'react-use';
import { Box } from '@opendoor/bricks/core';
import { useObservability } from 'shared/helpers/observability';

import { StepInfo, Wizard } from 'shared/components/Wizard';
import { ClosingTaskWizardLayout } from 'sellDirect/components/closingDash/ClosingTaskWizardLayout';
import { getSignersRows, WizardSummary } from 'sellDirect/components/closingDash/WizardSummary';
import { HelloSignWizardLayout } from 'sellDirect/components/closingDash/HelloSignWizardLayout';

import {
  HelloSignStep,
  ContractData,
} from 'sellDirect/components/closingDash/helloSign/HelloSignStep';
import { ClosingTasks } from 'sellDirect/helpers/closingTasks';
import { UnderContractOffer, ClosedOffer } from 'sellDirect/OfferContext';
import { getHubSellingRoute } from 'shared/helpers/routes';
import { useHistory } from 'platform/HistoryProvider';
import { SignContractResult } from 'sellDirect/graphql/useSignContract';
import { showToast } from '@opendoor/bricks/complex/Toast';
import { generatePath } from 'react-router-dom';
import { useQualificationContext } from 'shared/helpers/QualificationContext';

export const ADDENDUM_ID_QUERY_PARAM = 'addendum-id';

export interface GeneralAddendumWizardProps {
  offer: UnderContractOffer | ClosedOffer;
  closingTasks: ClosingTasks;
}

/**
 * Wizard for general addenda
 *
 * @param offer - Under contract offer
 * @param allAddenda - All addendum HelloSign tasks
 *
 * component-test-check-ignore
 * storybook-check-ignore
 */
export const GeneralAddendumWizard: FC<GeneralAddendumWizardProps> = ({ offer, closingTasks }) => {
  const { addressUUID } = useQualificationContext();
  const { trackError } = useObservability();
  const { history, goBack } = useHistory();
  const initialLocation = useMemo(() => history.location, [history]);
  const [contractData, setContractData] = useState<ContractData>();
  const addendumId = useSearchParam(ADDENDUM_ID_QUERY_PARAM);

  const allAddenda = closingTasks.signFormAddendums;
  const specificAddendum = allAddenda?.find((addendum) => addendum.id === addendumId);

  /** Track error if user navigates here but there's no additional addenda */
  if (!allAddenda?.length) {
    trackError('general-addendum--no-additional-addenda', undefined, { addendumId });
    history.replace(generatePath(getHubSellingRoute(), { addressUUID }));
  }

  /** Track error if we could not find addendum that matches the query param */
  if (!specificAddendum) {
    trackError('general-addendum--not-found', undefined, { addendumId, allAddenda });
    history.replace(generatePath(getHubSellingRoute(), { addressUUID }));
  }

  const steps: StepInfo[] = [
    {
      title: 'Addendum',
      content: ({ goToNextStep }) => (
        <ClosingTaskWizardLayout>
          <Box mt="10" mb="9">
            <WizardSummary
              offer={offer}
              rows={[...getSignersRows(closingTasks)]}
              helloSignTask={specificAddendum?.taskable}
              trackingName="general-addendum-summary"
              disableEdit
              onClickSignButton={(signer, helloSignUrl) => {
                setContractData({
                  signer,
                  helloSignUrl,
                });
                goToNextStep();
              }}
            />
          </Box>
        </ClosingTaskWizardLayout>
      ),
    },
    {
      title: `Sign ${offer.shouldSeeAgentExperience ? 'the' : 'your'} addendum`,
      // eslint-disable-next-line react/display-name
      content: ({ goToPreviousStep }) => {
        if (!contractData) {
          throw new Error('HelloSignStep expects contractData to be set');
        }
        return (
          <HelloSignWizardLayout>
            <HelloSignStep
              contractData={contractData}
              helloSignTaskName="signFormAddendums"
              offerId={offer.id}
              addendumId={addendumId || undefined}
              onTaskCompletion={(result) => {
                if (result === SignContractResult.success_fully_signed) {
                  history.push(generatePath(getHubSellingRoute(), { addressUUID }));
                } else if (result === SignContractResult.success_not_fully_signed) {
                  goToPreviousStep();
                } else {
                  goToPreviousStep();
                  showToast(
                    'error',
                    'Oops, something went wrong. Please try again in a few hours or contact your experience partner.',
                    'cake-hellosign-sign-addenda-error-toast',
                    { toastOptions: { position: 'bottom-left' } },
                  );
                }
              }}
            />
          </HelloSignWizardLayout>
        );
      },
    },
  ];

  return (
    <Wizard
      steps={steps}
      title="Addendum"
      hideFooter={!!contractData}
      onClose={() =>
        goBack({
          before: initialLocation,
          fallbackLocation: getHubSellingRoute(),
          addressUUID,
        })
      }
    />
  );
};
